import React from 'react';
import { ref, update } from 'firebase/database';
import { toast } from 'react-toastify';
import {
    FiActivity,
    FiCpu,
    FiHardDrive,
    FiThermometer,
    FiTrash2,
    FiTrendingUp,
    FiWifi,
} from 'react-icons/fi';

import { db } from '../../firebase';
import {
    cpuTempTone,
    healthStatusTone,
    latencyTone,
    ramTone,
    storageTone,
    toneClasses,
} from '../../utils/deviceData';

// Hero KPI strip for the device detail page.
//
// Renders one mini-tile per Phase-2 health metric (DEVICE-DATA.md §3),
// hiding any tile whose source field is missing rather than printing
// "N/A" walls. Sits above the active tab body so the most important
// numbers are visible from every tab.

export default function KpiStrip({ device, mac }) {
    const dd = device?.device_data;

    // "Free up space" runs pm trim-caches across all apps AND clears /sdcard/Download on the box.
    // App caches rebuild themselves; Download only holds transient MDM-pushed files on this fleet.
    // Sent as a plain STRING command (monitoring reads String.class).
    const freeUpSpace = async () => {
        if (!mac) return;
        try {
            await update(ref(db, `mdm/devices/${mac}`), { command: 'free_space', feedback: 'pending' });
            toast.success('Free-up-space sent (clears app caches + Downloads)');
        } catch (e) {
            toast.error(`Failed: ${e.message}`);
        }
    };

    if (!dd) return null;

    // Compute the values once. Each tile's data block describes its own
    // tone calc + label; tiles render only if the underlying metric is
    // present, keeping the strip tight on devices that don't report all
    // optional fields.
    const tiles = [];

    if (dd.health_score != null) {
        tiles.push({
            key: 'health',
            icon: <FiActivity />,
            label: 'Health',
            value: dd.health_score,
            suffix: '/ 100',
            tone: healthStatusTone(dd.health_status),
        });
    }

    if (dd.storage_total_gb && dd.storage_free_gb != null) {
        const pctFree = (dd.storage_free_gb / dd.storage_total_gb) * 100;
        tiles.push({
            key: 'storage',
            icon: <FiHardDrive />,
            label: 'Storage Free',
            value: `${dd.storage_free_gb.toFixed(1)} GB`,
            suffix: `${pctFree.toFixed(0)}%`,
            tone: storageTone(pctFree),
            action: mac ? (
                <button
                    onClick={freeUpSpace}
                    title="Free up space — evicts all app caches and clears /sdcard/Download"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-yellow-500/15 hover:bg-yellow-500/25 text-yellow-400 transition"
                >
                    <FiTrash2 /> Free up space
                </button>
            ) : null,
        });
    }

    if (dd.ram_total_mb && dd.ram_free_mb != null) {
        const pctFree = (dd.ram_free_mb / dd.ram_total_mb) * 100;
        tiles.push({
            key: 'ram',
            icon: <FiCpu />,
            label: 'RAM Free',
            value: `${dd.ram_free_mb} MB`,
            suffix: `${pctFree.toFixed(0)}%`,
            tone: ramTone(pctFree),
        });
    }

    if (dd.uptime_hours != null) {
        tiles.push({
            key: 'uptime',
            icon: <FiTrendingUp />,
            label: 'Uptime',
            value: formatUptime(dd.uptime_hours),
            tone: 'neutral',
        });
    }

    if (dd.network_quality?.latency_ms != null) {
        tiles.push({
            key: 'latency',
            icon: <FiWifi />,
            label: 'Latency',
            value: `${dd.network_quality.latency_ms} ms`,
            suffix: dd.network_type || '',
            tone: latencyTone(dd.network_quality.latency_ms),
        });
    }

    if (dd.performance?.cpu_temperature_c != null) {
        tiles.push({
            key: 'cpu_temp',
            icon: <FiThermometer />,
            label: 'CPU Temp',
            value: `${dd.performance.cpu_temperature_c}°C`,
            suffix: dd.performance.cpu_usage_percent != null ? `${dd.performance.cpu_usage_percent}% load` : '',
            tone: cpuTempTone(dd.performance.cpu_temperature_c),
        });
    }

    if (tiles.length === 0) return null;

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {tiles.map((tile) => (
                <KpiTile key={tile.key} {...tile} />
            ))}
        </div>
    );
}

function KpiTile({ icon, label, value, suffix, tone, action }) {
    const c = toneClasses(tone);
    return (
        <div className="card-chrome p-4 flex flex-col gap-1">
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <span className={c.text}>{icon}</span>
                {label}
            </div>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-baseline gap-2 min-w-0">
                    <span className={`text-xl font-semibold ${c.text}`}>{value}</span>
                    {suffix && <span className="text-xs text-gray-500">{suffix}</span>}
                </div>
                {action && <div className="shrink-0">{action}</div>}
            </div>
        </div>
    );
}

// 26h → "1d 2h", 50h → "2d 2h", 5h → "5h"
function formatUptime(hours) {
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    const rem = hours % 24;
    return rem === 0 ? `${days}d` : `${days}d ${rem}h`;
}
